/**
 * Cryptographic Utilities
 *
 * Web Crypto API wrappers for ECDSA P-256 operations
 * OPTIMIZED: Removed Buffer polyfill, using native browser APIs
 */

const ECDSA_P256_PARAMS: EcKeyGenParams = {
  name: "ECDSA",
  namedCurve: "P-256",
};

const ECDSA_SIGN_PARAMS: EcdsaParams = {
  name: "ECDSA",
  hash: { name: "SHA-256" },
};

/**
 * Generate ECDSA P-256 key pair
 * Exceeds Israeli standard (requires 160-bit, we use 256-bit)
 */
export async function generateECDSAKeyPair(): Promise<CryptoKeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    ECDSA_P256_PARAMS,
    true, // Extractable for storage
    ["sign", "verify"],
  );

  return keyPair;
}

/**
 * Export public key to Base64 SPKI format
 */
export async function exportPublicKey(publicKey: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey("spki", publicKey);
  return arrayBufferToBase64(exported);
}

/**
 * Import public key from Base64 SPKI
 */
export async function importPublicKey(base64Key: string): Promise<CryptoKey> {
  const keyData = base64ToArrayBuffer(base64Key);
  return await crypto.subtle.importKey(
    "spki",
    keyData,
    ECDSA_P256_PARAMS,
    true,
    ["verify"],
  );
}

/**
 * Export private key to Base64 PKCS8 (for encrypted storage)
 */
export async function exportPrivateKey(privateKey: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey("pkcs8", privateKey);
  return arrayBufferToBase64(exported);
}

/**
 * Import private key from Base64 PKCS8 (extractable for storage/backup)
 */
export async function importPrivateKey(base64Key: string): Promise<CryptoKey> {
  const keyData = base64ToArrayBuffer(base64Key);
  return await crypto.subtle.importKey(
    "pkcs8",
    keyData,
    ECDSA_P256_PARAMS,
    true,
    ["sign"],
  );
}

/**
 * Import private key from Base64 PKCS8 as non-extractable
 * Use for runtime operations where key extraction is not needed
 */
export async function importPrivateKeyNonExtractable(
  base64Key: string,
): Promise<CryptoKey> {
  const keyData = base64ToArrayBuffer(base64Key);
  return await crypto.subtle.importKey(
    "pkcs8",
    keyData,
    ECDSA_P256_PARAMS,
    false, // Non-extractable
    ["sign"],
  );
}

/**
 * Calculate SHA-256 hash of data
 */
export async function calculateSHA256(
  data: string | ArrayBuffer,
): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const dataBuffer = typeof data === "string" ? encoder.encode(data) : data;
  return await crypto.subtle.digest("SHA-256", dataBuffer);
}

/**
 * Sign data with ECDSA
 */
export async function signWithECDSA(
  privateKey: CryptoKey,
  data: string | ArrayBuffer,
): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const dataBuffer = typeof data === "string" ? encoder.encode(data) : data;
  return await crypto.subtle.sign(ECDSA_SIGN_PARAMS, privateKey, dataBuffer);
}

/**
 * Verify ECDSA signature
 */
export async function verifyECDSA(
  publicKey: CryptoKey,
  signature: ArrayBuffer,
  data: string | ArrayBuffer,
): Promise<boolean> {
  const encoder = new TextEncoder();
  const dataBuffer = typeof data === "string" ? encoder.encode(data) : data;
  return await crypto.subtle.verify(
    ECDSA_SIGN_PARAMS,
    publicKey,
    signature,
    dataBuffer,
  );
}

/**
 * Generate key fingerprint (SHA-256 of SPKI)
 */
export async function generateKeyFingerprint(
  publicKey: CryptoKey,
): Promise<string> {
  const exported = await crypto.subtle.exportKey("spki", publicKey);
  const hash = await crypto.subtle.digest("SHA-256", exported);
  return arrayBufferToHex(hash).substring(0, 32); // First 16 bytes
}

/**
 * Convert ArrayBuffer to hexadecimal string
 */
export function arrayBufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Convert ArrayBuffer to Base64 using native browser API
 * OPTIMIZED: Replaced Buffer polyfill with btoa/atob
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert Base64 to ArrayBuffer using native browser API
 * OPTIMIZED: Replaced Buffer polyfill with btoa/atob
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ============================================================================
// PASSWORD-BASED ENCRYPTION (PBKDF2 + AES-GCM)
// ============================================================================

// PBKDF2 iteration counts for key derivation
// Version 1: Legacy 100k iterations (OWASP 2017 baseline)
// Version 2: 600k iterations (OWASP 2023 recommendation for SHA-256)
const PBKDF2_ITERATIONS_V1 = 100000;
const PBKDF2_ITERATIONS_V2 = 600000;
const CURRENT_VERSION = 2;

const SALT_LENGTH = 16; // 128 bits
const IV_LENGTH = 12; // 96 bits for GCM
const KEY_LENGTH = 256; // AES-256

export interface EncryptedData {
  ciphertext: string; // Base64 encrypted data
  salt: string; // Base64 salt
  iv: string; // Base64 IV
  version: number;
  iterations?: number; // Track PBKDF2 iterations for backward compatibility (600000 or 100000)
}

/**
 * Derive AES key from password using PBKDF2
 * @param password - The password to derive the key from
 * @param salt - The salt for key derivation
 * @param iterations - The number of PBKDF2 iterations (version-dependent)
 */
export async function deriveKeyFromPassword(
  password: string,
  salt: ArrayBuffer,
  iterations: number = PBKDF2_ITERATIONS_V2,
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordData = encoder.encode(password);

  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    passwordData,
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );

  // Derive AES-GCM key using PBKDF2
  return await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: iterations,
      hash: { name: "SHA-256" },
    },
    keyMaterial,
    { name: "AES-GCM", length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Encrypt data with password using PBKDF2 + AES-GCM
 */
export async function encryptWithPassword(
  plaintext: string,
  password: string,
): Promise<EncryptedData> {
  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  // Derive key from password using current version's iteration count
  const key = await deriveKeyFromPassword(
    password,
    salt.buffer,
    PBKDF2_ITERATIONS_V2,
  );

  // Encrypt the data
  const encoder = new TextEncoder();
  const plaintextData = encoder.encode(plaintext);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    key,
    plaintextData,
  );

  return {
    ciphertext: arrayBufferToBase64(ciphertext),
    salt: arrayBufferToBase64(salt.buffer),
    iv: arrayBufferToBase64(iv.buffer),
    version: CURRENT_VERSION,
    iterations: PBKDF2_ITERATIONS_V2, // Store iteration count for future flexibility
  };
}

/**
 * Decrypt data with password using PBKDF2 + AES-GCM
 * Supports version 1 (100k iterations) and version 2 (600k iterations)
 * for backward compatibility with existing encrypted keys
 */
export async function decryptWithPassword(
  encryptedData: EncryptedData,
  password: string,
): Promise<string> {
  // Convert Base64 back to ArrayBuffer
  const salt = base64ToArrayBuffer(encryptedData.salt);
  const iv = base64ToArrayBuffer(encryptedData.iv);
  const ciphertext = base64ToArrayBuffer(encryptedData.ciphertext);

  // Determine iteration count: use stored iterations if available, otherwise fall back to version-based logic
  // This provides backward compatibility with legacy encrypted keys (100k iterations)
  const iterations =
    encryptedData.iterations ??
    (encryptedData.version && encryptedData.version >= 2
      ? PBKDF2_ITERATIONS_V2
      : PBKDF2_ITERATIONS_V1);

  // Derive key from password using version-specific iteration count
  const key = await deriveKeyFromPassword(password, salt, iterations);

  // Decrypt the data
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv },
    key,
    ciphertext,
  );

  // Convert decrypted data back to string
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

/**
 * HMAC-SHA256 for audit log signing (SEC-REQ-007)
 * Creates a message authentication code using SHA-256
 */
export async function hmacSha256(
  message: string,
  key: string,
): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(message);

  // Import key
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  // Sign
  return await crypto.subtle.sign("HMAC", cryptoKey, messageData);
}

/**
 * Generate random bytes for cryptographic operations
 */
export function generateRandomBytes(length: number): Uint8Array {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return array;
}
